import FileSaver from 'file-saver';
import axios, { AxiosResponse } from "axios";
import { appContext } from "../AppContext";
import { ApiError } from "@iconics/core";
import { IbssRequestConfig } from "@iconics/core";

export default class LegacyApiClient
{
    private static get webEntryApiV1() { return appContext().settings.dataEntryApi + '/api/webentry/v1/'; }

    // +----------------+
    // | USER ENDPOINTS |
    // +----------------+

    public static async getPrivacyPolicy(): Promise<string>
    {
        try
        {
            const axiosConfig: IbssRequestConfig = { suppressErrorPopup: true };
            const response = await axios.get<string>(`${appContext().settings.platformApi}/v1/Service/privacy-policy`, axiosConfig);
            return response.data;
        }
        catch (response)
        {
            throw ApiError.fromAxiosResponse(response as AxiosResponse);
        }
    }

    // +----------------+
    // | FILE ENDPOINTS |
    // +----------------+

    public static async getFiles(path: string): Promise<IFile[]>
    {
        try
        {
            const response = await axios.get<IFile[]>(`${appContext().settings.dataEntryApi}/api/webentry/v1/file/${path}`);
            return response.data;
        }
        catch (response)
        {
            throw ApiError.fromAxiosResponse(response as AxiosResponse);
        }
    }

    public static getFiles2(path: string): Promise<AxiosResponse<{
        name: string;
        path: string;
        lastModified: string;
        createdAt: string;
        size: string;
    }[]>>
    {
        return axios.get(`${LegacyApiClient.webEntryApiV1}/file/${path}`);
    }

    public static uploadFile(model: {
        fileData: FormData,
        modalPathValue: string,
        processAsMapvalue: boolean,
    }): Promise<void>
    {
        const { fileData, modalPathValue, processAsMapvalue } = model
        return axios.post(`${LegacyApiClient.webEntryApiV1}/File/${modalPathValue}?ProcessAsMapFile=${processAsMapvalue}`, fileData);
    }

    public static async downloadFile(path: string): Promise<void>
    {
        const fileName = path.split("/").pop();
        const response = await axios.get(`${LegacyApiClient.webEntryApiV1}/file/Download/${path}`, { responseType: "blob" });
        let blob = new Blob([response.data], { type: "text/plain;charset=utf-8" });
        FileSaver.saveAs(blob, fileName);
    }

    public static deleteFile(path: string): Promise<void>
    {
        return axios.delete(`${LegacyApiClient.webEntryApiV1}/file/${path}`);
    }

    // +------------------------+
    // | NOTIFICATION ENDPOINTS |
    // +------------------------+

    public static async getActiveNotifications(nodeId: number): Promise<IActiveNotification[]>
    {
        try
        {
            const axiosConfig: IbssRequestConfig = { suppressErrorPopup: true };
            const response = await axios.get<IActiveNotification[]>(`${appContext().settings.platformApi}/v1/${nodeId}/Notifications/active?recursive=true&%24top=50&%24select=Notification_Id,ImageURI,ActionURI,Notification_Title,Notification_Priority,Classification,Subclassification,Notification_Message,Available_From,Notification_Record_Id,Notification_Short_Message,Node_Id,Notification_Status,Notification_User_Email`, axiosConfig);
            return response.data;
        }
        catch (error)
        {
            throw console.log(error, 'error');
        }
    }

    // +-------------------+
    // | VERSION ENDPOINTS |
    // +-------------------+

    public static async getDataApiVersion(): Promise<string>
    {
        try
        {
            const response = await axios.get<{ version: string }>(`${appContext().settings.dataApi}/v1/Service/version`);
            return response.data.version;
        }
        catch (response)
        {
            throw ApiError.fromAxiosResponse(response as AxiosResponse);
        }
    }

    public static async getPlatformApiVersion(): Promise<string>
    {
        try
        {
            const response = await axios.get<{ Version: string }>(`${appContext().settings.platformApi}/v1/Service/version`);
            return response.data.Version;
        }
        catch (response)
        {
            throw ApiError.fromAxiosResponse(response as AxiosResponse);
        }
    }
}

export interface IFile
{
    name: string;
    path: string;
    lastModified: string;
    createdAt: string;
    size: string;
}

export interface IActiveNotification
{
    Notification_Id: number;
    ImageURI: string;
    ActionURI: string;
    Notification_Title: string;
    Notification_Priority: number;
    Classification: string;
    Subclassification: string;
    Notification_Message: string;
    Available_From: string;
    Expiry_Date: string;
    Node_Id: number;
    Notification_Record_Id: string;
    Notification_Short_Message: string;
    Notification_Status: string;
    Notification_User_Email: string
}
